import React, { createContext, useContext } from 'react';
import { View, Text, type ViewProps, type TextProps } from 'react-native';
import { cn } from '@/utils/cn';

export type AlertVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

export interface AlertProps extends ViewProps {
  children: React.ReactNode;
  variant?: AlertVariant;
  className?: string;
}

const alertVariants: Record<AlertVariant, string> = {
  default: 'bg-card border-border',
  success: 'bg-success-50 border-success-200',
  warning: 'bg-warning-50 border-warning-200',
  error: 'bg-error-50 border-error-200',
  info: 'bg-info-50 border-info-200',
};

/** No nativo `text-*` numa View não chega aos textos: a cor vai em cada Text. */
const alertTextVariants: Record<AlertVariant, string> = {
  default: 'text-foreground',
  success: 'text-success-900',
  warning: 'text-warning-900',
  error: 'text-error-900',
  info: 'text-info-900',
};

const AlertTextContext = createContext(alertTextVariants.default);

export function Alert({ children, variant = 'default', className, ...props }: AlertProps) {
  return (
    <View
      className={cn('relative w-full rounded-lg border p-4', alertVariants[variant], className)}
      {...props}
    >
      <AlertTextContext.Provider value={alertTextVariants[variant]}>
        {children}
      </AlertTextContext.Provider>
    </View>
  );
}

export interface AlertTitleProps extends TextProps {
  children: React.ReactNode;
  className?: string;
}

export function AlertTitle({ children, className, ...props }: AlertTitleProps) {
  const textClass = useContext(AlertTextContext);
  return (
    <Text
      className={cn(
        'mb-1 font-semibold text-base leading-none tracking-tight',
        textClass,
        className,
      )}
      {...props}
    >
      {children}
    </Text>
  );
}

export interface AlertDescriptionProps extends TextProps {
  children: React.ReactNode;
  className?: string;
}

export function AlertDescription({ children, className, ...props }: AlertDescriptionProps) {
  const textClass = useContext(AlertTextContext);
  return (
    <Text className={cn('text-sm [&_p]:leading-relaxed', textClass, className)} {...props}>
      {children}
    </Text>
  );
}
